/**
 * Authentication Service for Keycloak Integration
 */

import axios from 'axios';

const KEYCLOAK_URL = process.env.REACT_APP_KEYCLOAK_URL || 'http://103.118.16.53:9090/';
const KEYCLOAK_REALM = process.env.REACT_APP_KEYCLOAK_REALM || 'carex';
const KEYCLOAK_CLIENT_ID = process.env.REACT_APP_KEYCLOAK_CLIENT_ID || 'carex-api';
const KEYCLOAK_CLIENT_SECRETE="CWdrJwMrdwkIuNr4KeX3sqYKH3HG2r3p";


interface KeycloakTokenResponse {
  access_token: string;
  expires_in: number;
  refresh_expires_in: number;
  refresh_token: string;
  token_type: string;
  id_token?: string;
  'not-before-policy': number;
  session_state: string;
  scope: string;
}

interface DecodedToken {
  sub: string;
  exp?: number;
  iat?: number;
  email_verified?: boolean;
  name?: string;
  preferred_username?: string;
  given_name?: string;
  family_name?: string;
  email?: string;
  realm_access?: {
    roles: string[];
  };
  resource_access?: {
    [key: string]: {
      roles: string[];
    };
  };
}

class AuthService {
  private tokenEndpoint = `${KEYCLOAK_URL}realms/${KEYCLOAK_REALM}/protocol/openid-connect/token`;
  private logoutEndpoint = `${KEYCLOAK_URL}realms/${KEYCLOAK_REALM}/protocol/openid-connect/logout`;

  /**
   * Login with username and password
   */
  async login(username: string, password: string): Promise<{ token: string; user: any }> {
    try {
      const response = await axios.post<KeycloakTokenResponse>(
        this.tokenEndpoint,
        new URLSearchParams({
          grant_type: 'password',
          client_id: KEYCLOAK_CLIENT_ID,
          username,
          password,
          client_secret:KEYCLOAK_CLIENT_SECRETE
        }),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        }
      );

      const { access_token, refresh_token } = response.data;

      // Decode the JWT token to get user info
      const decodedToken = this.decodeToken(access_token);

      // Store tokens
      localStorage.setItem('authToken', access_token);
      localStorage.setItem('refreshToken', refresh_token);

      // Determine user role from token
      const role = this.extractRole(decodedToken);

      const user = {
        id: decodedToken.sub,
        username: decodedToken.preferred_username || username,
        email: decodedToken.email,
        name: decodedToken.name || `${decodedToken.given_name} ${decodedToken.family_name}`,
        role,
        token: access_token,
      };

      return { token: access_token, user };
    } catch (error: any) {
      console.error('Login error:', error);
      if (error.response?.status === 401) {
        throw new Error('Invalid username or password');
      }
      throw new Error('Authentication failed. Please try again.');
    }
  }

  /**
   * Refresh the access token using refresh token
   */
  async refreshToken(): Promise<string | null> {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      return null;
    }

    try {
      const response = await axios.post<KeycloakTokenResponse>(
        this.tokenEndpoint,
        new URLSearchParams({
          grant_type: 'refresh_token',
          client_id: KEYCLOAK_CLIENT_ID,
          refresh_token: refreshToken,
        }),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        }
      );

      const { access_token, refresh_token: newRefreshToken } = response.data;

      localStorage.setItem('authToken', access_token);
      localStorage.setItem('refreshToken', newRefreshToken);

      return access_token;
    } catch (error) {
      console.error('Token refresh failed:', error);
      this.logout();
      return null;
    }
  }

  /**
   * Logout from Keycloak
   */
  async logout(): Promise<void> {
    const refreshToken = localStorage.getItem('refreshToken');

    if (refreshToken) {
      try {
        await axios.post(
          this.logoutEndpoint,
          new URLSearchParams({
            client_id: KEYCLOAK_CLIENT_ID,
            refresh_token: refreshToken,
          }),
          {
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
          }
        );
      } catch (error) {
        console.error('Logout error:', error);
      }
    }

    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  }

  /**
   * Decode JWT token
   */
  private decodeToken(token: string): DecodedToken {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding token:', error);
      throw new Error('Invalid token');
    }
  }

  /**
   * Extract user role from token
   */
  private extractRole(decodedToken: DecodedToken): string {
    // Check realm roles
    const realmRoles = decodedToken.realm_access?.roles || [];

    if (realmRoles.includes('super_admin') || realmRoles.includes('SUPER_ADMIN')) {
      return 'SUPER_ADMIN';
    }
    if (realmRoles.includes('client_admin') || realmRoles.includes('CLIENT_ADMIN')) {
      return 'CLIENT_ADMIN';
    }
    if (realmRoles.includes('facility_admin') || realmRoles.includes('FACILITY_ADMIN')) {
      return 'FACILITY_ADMIN';
    }
    if (realmRoles.includes('admin') || realmRoles.includes('ADMIN')) {
      return 'ADMIN';
    }
    if (realmRoles.includes('doctor') || realmRoles.includes('DOCTOR')) {
      return 'DOCTOR';
    }
    if (realmRoles.includes('nurse') || realmRoles.includes('NURSE')) {
      return 'NURSE';
    }
    if (realmRoles.includes('social_worker') || realmRoles.includes('SOCIAL_WORKER')) {
      return 'SOCIAL_WORKER';
    }

    // Default role
    return 'NURSE';
  }

  /**
   * Check if token is expired
   */
  isTokenExpired(token: string): boolean {
    try {
      const decoded = this.decodeToken(token);
      const currentTime = Date.now() / 1000;
      return decoded['exp'] ? decoded['exp'] < currentTime : true;
    } catch {
      return true;
    }
  }
}

export default new AuthService();
